import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { StyleRoot } from 'radium';
import { breakpoints } from '@hooks/useBreakpoints';
import debounce from 'lodash/debounce';
import calculateFontSize from '../../utils/calculateFontSize';
import s from './ScaleText.module.scss';

const propTypes = {
  text: PropTypes.array,
  multiplier: PropTypes.number,
  lineMultiplier: PropTypes.number,
  fontFamily: PropTypes.string,
  minFontSize: PropTypes.number,
  maxFontSizes: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    ss: PropTypes.number,
    md: PropTypes.number,
    xm: PropTypes.number,
    lg: PropTypes.number,
    lgg: PropTypes.number,
    xl: PropTypes.number,
    xxl: PropTypes.number,
  }),
};

const defaultMaxSize = {};
const defaultText = [];

const ScaleText = ({
  text = defaultText,
  multiplier = 0.9,
  lineMultiplier = 1.3,
  fontFamily = 'sans-serif',
  maxFontSizes = defaultMaxSize,
  minFontSize = 9,
}) => {
  const containerRef = useRef(null);
  const [styleObject, setStyleObject] = useState({});

  useEffect(() => {
    // istanbul ignore next
    if (!containerRef.current) return () => {};

    const scaleTextSize = () => {
      const paddedWidth = containerRef.current?.offsetWidth;
      const calculatedSize = calculateFontSize({
        text,
        fontFamily,
        paddedWidth,
        multiplier,
      });
      const maxSizeBreakpoints = Object.keys(maxFontSizes);
      let style = {};

      maxSizeBreakpoints.forEach((key) => {
        let sizeCalc = calculatedSize;
        sizeCalc = Math.max(sizeCalc, minFontSize);
        sizeCalc = Math.min(sizeCalc, maxFontSizes[key]);

        style[`@media (min-width: ${breakpoints[key]}px)`] = {
          fontSize: `${sizeCalc}px`,
          lineHeight: `${sizeCalc * lineMultiplier}px`,
        };
      });

      if (maxSizeBreakpoints.length === 0) {
        let sizeCalc = calculatedSize;
        sizeCalc = Math.max(sizeCalc, minFontSize);
        style = {
          fontSize: `${sizeCalc}px`,
          lineHeight: `${sizeCalc * lineMultiplier}px`,
        };
      }

      setStyleObject(style);
    };

    scaleTextSize();
    const debouncedScaleText = debounce(scaleTextSize, 100);
    window.addEventListener('resize', debouncedScaleText);

    return () => {
      window.removeEventListener('resize', debouncedScaleText);
    };
  }, [fontFamily, lineMultiplier, maxFontSizes, minFontSize, multiplier, text]);

  return (
    <StyleRoot>
      <div className={s.root}>
        <div ref={containerRef} className={s.container} style={styleObject}>
          {text.map((line) => {
            return (
              <div className={s.line} key={line}>
                {line}
              </div>
            );
          })}
        </div>
      </div>
    </StyleRoot>
  );
};

ScaleText.propTypes = propTypes;
export default ScaleText;
